import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useQueueTimes } from '../hooks/useQueueTimes';
import { useVisit, useUpdateVisit } from '../hooks/useVisit';
import { useTrips } from '../hooks/useTrips';
import { useAttractions } from '../hooks/useAttractions';
import { useAddAttraction, useRemoveAttraction, useToggleComplete, useReorderAttractions } from '../hooks/useQueueTimes';
import { CompactAttractionRow } from '../design-system/primitives/CompactAttractionRow';
import { EmptyState } from '../components/shared/EmptyState';
import { SwipeableCard } from '../components/shared/SwipeableCard';
import { PullToRefresh } from '../components/shared/PullToRefresh';
import { SearchInput } from '../components/shared/SearchInput';
import { Mountain, ChevronDown, Plus, ArrowLeft } from 'lucide-react';
import { getDirections } from '../utils/mapDirections';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

function getPeriodInfo(period: 'MORNING' | 'MIDDAY' | 'EVENING') {
  switch (period) {
    case 'MORNING': return { label: 'Morning', color: 'text-amber-500 dark:text-amber-300' };
    case 'MIDDAY': return { label: 'Midday', color: 'text-orange-500 dark:text-orange-300' };
    case 'EVENING': return { label: 'Evening', color: 'text-indigo-500 dark:text-indigo-300' };
  }
}

function PeriodPill({ period, onClick }: { period: 'MORNING' | 'MIDDAY' | 'EVENING'; onClick?: () => void }) {
  const { label, color } = getPeriodInfo(period);
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${onClick ? 'cursor-pointer hover:opacity-80' : ''} ${color}`}
      onClick={onClick} title={onClick ? 'Click to change' : undefined}>
      {label}
      {onClick && <ChevronDown className="w-3 h-3 opacity-60" />}
    </span>
  );
}

function PeriodPicker({ current, onChange, onClose }: {
  current: 'MORNING' | 'MIDDAY' | 'EVENING';
  onChange: (p: 'MORNING' | 'MIDDAY' | 'EVENING') => void;
  onClose: () => void;
}) {
  const [hovered, setHovered] = React.useState<'MORNING' | 'MIDDAY' | 'EVENING' | null>(null);
  const periods: ('MORNING' | 'MIDDAY' | 'EVENING')[] = ['MORNING', 'MIDDAY', 'EVENING'];
  const active = hovered ?? current;
  return (
    <div className="absolute top-full left-0 mt-1 z-20 bg-white dark:bg-surface-dark-card rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-1.5 min-w-[160px]" role="dialog" aria-label="Select start period">
      {periods.map((period) => {
        const isActive = period === active;
        const { label } = getPeriodInfo(period);
        return (
          <button key={period} onClick={() => onChange(period)}
            onMouseEnter={() => setHovered(period)} onMouseLeave={() => setHovered(null)}
            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-left transition-colors text-sm ${isActive ? 'bg-disney-blue-500 text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
            <span>{label}</span>
          </button>
        );
      })}
      <button onClick={onClose} className="w-full mt-0.5 px-3 py-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md transition-colors">Cancel</button>
    </div>
  );
}

export function VisitDetailPageNew(): React.ReactElement {
  const { visitId } = useParams<{ visitId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromHome = (location.state as any)?.from === 'home';
  const queryClient = useQueryClient();
  const [showAddAttraction, setShowAddAttraction] = React.useState(false);
  const [selectedEntityType, setSelectedEntityType] = React.useState('ALL');
  const [selectedPriority, setSelectedPriority] = React.useState('ALL');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [periodPickerOpen, setPeriodPickerOpen] = React.useState(false);
  const [isManualOrder, setIsManualOrder] = React.useState(false);
  const [manualOrder, setManualOrder] = React.useState<string[]>([]);
  const [reorderMode, setReorderMode] = React.useState(false);

  const { data: visit } = useVisit(visitId ?? null);
  const updateVisit = useUpdateVisit();
  const addAttraction = useAddAttraction();
  const removeAttraction = useRemoveAttraction();
  const toggleComplete = useToggleComplete();
  const reorderMutation = useReorderAttractions(visitId ?? '');
  const { data: queueTimes, isLoading: queueLoading } = useQueueTimes(visitId ?? null);
  const { data: trips } = useTrips();
  const { data: attractions } = useAttractions(visit?.parkName ?? null);

  const handleRefresh = React.useCallback(async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['queue-times', visitId] }),
      queryClient.refetchQueries({ queryKey: ['visit', visitId] }),
    ]);
  }, [queryClient, visitId]);

  const handleDirections = React.useCallback((attractionName: string) => {
    const attraction = attractions?.find((a) => a.name === attractionName);
    if (!attraction?.latitude || !attraction?.longitude) {
      alert('Coordinates not available for this attraction');
      return;
    }
    getDirections({
      attractionName: attraction.name,
      attractionLat: attraction.latitude,
      attractionLng: attraction.longitude,
    });
  }, [attractions]);

  const handlePeriodChange = (period: 'MORNING' | 'MIDDAY' | 'EVENING') => {
    if (!visitId) return;
    updateVisit.mutate(
      { visitId, data: { visitPeriod: period } },
      { onSettled: () => setPeriodPickerOpen(false) }
    );
  };

  const trip = trips?.find((t) => visit && t.id === visit.tripId);
  const addedNames = React.useMemo(() => new Set(queueTimes?.map((qt) => qt.attractionName) ?? []), [queueTimes]);
  const availableAttractions: any[] = React.useMemo(() => attractions?.filter((a) => !addedNames.has(a.name)) ?? [], [attractions, addedNames]);

  const entityTypes = Array.from(new Set(availableAttractions.map((a: any) => a.entityType))).sort();
  const priorityCounts: Record<string, number> = { ALL: availableAttractions.length, HIGH: 0, MEDIUM: 0, LOW: 0 };
  availableAttractions.forEach((a: any) => {
    const p = a.priority || 'MEDIUM';
    if (p in priorityCounts) priorityCounts[p]++;
  });

  const activeQueueTimes = React.useMemo(() => queueTimes?.filter((qt) => !qt.completed) ?? [], [queueTimes]);
  const completedQueueTimes = React.useMemo(() => queueTimes?.filter((qt) => qt.completed) ?? [], [queueTimes]);
  const shortestWaitItem = activeQueueTimes
    .filter((qt) => qt.waitTimeMinutes != null)
    .reduce<typeof activeQueueTimes[0] | null>(
      (best, qt) =>
        best === null || (qt.waitTimeMinutes! < best.waitTimeMinutes!) ? qt : best,
      null,
    );
  const shortestWaitName = shortestWaitItem?.attractionName ?? null;

  const sortedActive = React.useMemo(() => {
    if (isManualOrder && manualOrder.length > 0) {
      const byName = new Map(activeQueueTimes.map((qt) => [qt.attractionName, qt]));
      const ordered = manualOrder.flatMap((name) => {
        const item = byName.get(name);
        return item ? [item] : [];
      });
      const orderedNames = new Set(manualOrder);
      const remaining = activeQueueTimes.filter((qt) => !orderedNames.has(qt.attractionName));
      return [...ordered, ...remaining];
    }
    const positionOf = new Map(activeQueueTimes.map((qt, i) => [qt.attractionName, i]));
    const priorityRank = (p: string) => p === 'HIGH' ? 0 : p === 'LOW' ? 2 : 1;
    return [...activeQueueTimes].sort((a, b) => {
      const posDiff = (positionOf.get(a.attractionName) ?? Infinity) - (positionOf.get(b.attractionName) ?? Infinity);
      if (posDiff !== 0) return posDiff;
      const priDiff = priorityRank(a.priority) - priorityRank(b.priority);
      if (priDiff !== 0) return priDiff;
      return (b.rating ?? 0) - (a.rating ?? 0);
    });
  }, [activeQueueTimes, isManualOrder, manualOrder]);

  const handleMoveAttraction = React.useCallback((index: number, direction: 'up' | 'down') => {
    const newOrder = [...sortedActive];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newOrder.length) return;
    [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
    const names = newOrder.map((qt) => qt.attractionName);
    setManualOrder(names);
    // Optimistic update
    queryClient.setQueryData(['queue-times', visitId], (old: any[]) => {
      if (!old) return old;
      const completedItems = old.filter((item: any) => item.completed);
      const activeItems = old.filter((item: any) => !item.completed);
      const byName = new Map(activeItems.map((item: any) => [item.attractionName, item]));
      const reordered = names.flatMap((name) => {
        const item = byName.get(name);
        return item ? [item] : [];
      });
      return [...reordered, ...completedItems];
    });
    reorderMutation.mutate(names, {
      onError: () => {
        queryClient.invalidateQueries({ queryKey: ['queue-times', visitId] });
      },
    });
    setIsManualOrder(true);
  }, [sortedActive, queryClient, visitId, reorderMutation]);

  const sortedCompleted = React.useMemo(() => {
    return [...completedQueueTimes].sort((a, b) => a.attractionName.localeCompare(b.attractionName));
  }, [completedQueueTimes]);

  const filteredAndSortedAttractions = React.useMemo(() => {
    let list = [...availableAttractions];
    if (selectedEntityType !== 'ALL') list = list.filter((a) => a.entityType === selectedEntityType);
    if (selectedPriority !== 'ALL') list = list.filter((a) => (a.priority || 'MEDIUM') === selectedPriority);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(q));
    }
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [availableAttractions, selectedEntityType, selectedPriority, searchQuery]);


  if (!visit) {
    return (
      <div className="min-h-screen bg-surface-light dark:bg-surface-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">Visit not found</p>
          <button onClick={() => navigate('/')} className="btn btn-primary mt-4">Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-6">
        <button onClick={() => fromHome ? navigate('/') : trip ? navigate(`/trip/${trip.id}`) : navigate('/')}
          className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <div className="relative bg-white dark:bg-surface-dark-card rounded-2xl p-5 shadow-elevated text-left border-l-4 border-disney-gold-400 dark:ring-1 dark:ring-white/[0.08]">
          {trip && (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-right mb-2">
              Part of: <span className="text-disney-gold-600 dark:text-disney-gold-400 font-medium">{trip.name}</span>
            </p>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">{visit.parkName}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{formatDate(visit.visitDate)}</p>
            {visit.visitPeriod && (
              <div className="mt-1.5 relative inline-block">
                <PeriodPill period={visit.visitPeriod} onClick={() => setPeriodPickerOpen(true)} />
                {periodPickerOpen && (
                  <PeriodPicker current={visit.visitPeriod} onChange={handlePeriodChange} onClose={() => setPeriodPickerOpen(false)} />
                )}
              </div>
            )}
          </div>
        </div>

        {queueLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton-queue-item">
                <div className="skeleton-queue-info">
                  <div className="skeleton-queue-header skeleton" />
                  <div className="skeleton-queue-status skeleton" />
                </div>
                <div className="skeleton-queue-actions">
                  <div className="skeleton-wait-time skeleton" />
                  <div className="skeleton-remove-button skeleton" />
                </div>
              </div>
            ))}
          </div>
        ) : queueTimes && queueTimes.length > 0 ? (
          <div className="space-y-6">
            {sortedActive.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2 ml-1 mr-1">
                  <h2 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Active</h2>
                  <button
                    onClick={() => setReorderMode((m) => !m)}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors ${reorderMode ? 'bg-disney-blue-100 dark:bg-disney-blue-900/30 text-disney-blue-700 dark:text-disney-blue-300 font-medium' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
                    aria-label={reorderMode ? 'Disable reordering' : 'Enable reordering'}
                    aria-pressed={reorderMode}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                    Reorder
                  </button>
                </div>
                <div className="space-y-3">
                  {sortedActive.map((qt, index) => (
                    <SwipeableCard key={qt.attractionName}
                      onSwipeLeft={() => removeAttraction.mutate({ visitId: visitId!, attractionName: qt.attractionName })}
                      onSwipeRight={() => toggleComplete.mutate({ visitId: visitId!, attractionName: qt.attractionName, completed: !qt.completed })}
                      isCompleted={qt.completed}
                      onDirections={() => handleDirections(qt.attractionName)}>
                      <div className="flex items-center">
                        <div className="flex-1 min-w-0">
                          <CompactAttractionRow
                            attractionName={qt.attractionName}
                            waitMinutes={qt.waitTimeMinutes ?? null}
                            isCompleted={qt.completed}
                            isShortestWait={qt.attractionName === shortestWaitName}
                            entityType={qt.entityType}
                          />
                        </div>
                        {reorderMode && (
                          <div className="flex flex-col gap-0.5 px-1.5 flex-shrink-0 self-center">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleMoveAttraction(index, 'up'); }}
                              className={`p-0.5 rounded transition-colors ${index === 0 ? 'invisible pointer-events-none' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                              aria-hidden={index === 0}
                              tabIndex={index === 0 ? -1 : 0}
                              aria-label="Move up">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleMoveAttraction(index, 'down'); }}
                              className={`p-0.5 rounded transition-colors ${index === sortedActive.length - 1 ? 'invisible pointer-events-none' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                              aria-hidden={index === sortedActive.length - 1}
                              tabIndex={index === sortedActive.length - 1 ? -1 : 0}
                              aria-label="Move down">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </SwipeableCard>
                  ))}
                </div>
              </div>
            )}

            {sortedCompleted.length > 0 && (
              <div>
                <h2 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Completed</h2>
                <div className="space-y-3">
                  {sortedCompleted.map((qt) => (
                    <SwipeableCard key={qt.attractionName}
                      onSwipeLeft={() => removeAttraction.mutate({ visitId: visitId!, attractionName: qt.attractionName })}
                      onSwipeRight={() => toggleComplete.mutate({ visitId: visitId!, attractionName: qt.attractionName, completed: !qt.completed })}
                      isCompleted={qt.completed}
                      onDirections={() => handleDirections(qt.attractionName)}>
                      <CompactAttractionRow
                        attractionName={qt.attractionName}
                        waitMinutes={qt.waitTimeMinutes ?? null}
                        isCompleted={qt.completed}
                        isShortestWait={false}
                        entityType={qt.entityType}
                      />
                    </SwipeableCard>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <EmptyState icon={Mountain} iconColor="text-disney-blue-400"
            title="No attractions yet" subtitle="Add attractions to your visit to track wait times." />
        )}

        {showAddAttraction ? (
          <div className="bg-white dark:bg-surface-dark-card rounded-xl p-4 shadow-card dark:ring-1 dark:ring-white/[0.06] space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Add Attractions</h3>
              <button onClick={() => { setShowAddAttraction(false); setSelectedEntityType('ALL'); setSelectedPriority('ALL'); setSearchQuery(''); }}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <SearchInput value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery('')} placeholder="Search..." />
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Type</p>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setSelectedEntityType('ALL')}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${selectedEntityType === 'ALL' ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                  All ({availableAttractions.length})
                </button>
                {entityTypes.map((type) => (
                  <button key={type} onClick={() => setSelectedEntityType(type)}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${selectedEntityType === type ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                    {type} ({availableAttractions.filter((a) => a.entityType === type).length})
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Priority</p>
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setSelectedPriority('ALL')}
                  className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${selectedPriority === 'ALL' ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                  All ({priorityCounts.ALL})
                </button>
                {(['HIGH', 'MEDIUM', 'LOW'] as const).map((priority) => (
                  <button key={priority} onClick={() => setSelectedPriority(priority)}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${selectedPriority === priority ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                    {priority} ({priorityCounts[priority]})
                  </button>
                ))}
              </div>
            </div>
            {availableAttractions.length === 0 && selectedEntityType === "ALL" && selectedPriority === "ALL" && !searchQuery ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">All attractions added!</p>
            ) : filteredAndSortedAttractions.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No attractions found</p>
            ) : (
              <ul className="space-y-1.5 max-h-60 overflow-y-auto">
                {filteredAndSortedAttractions.map((attraction) => (
                  <li key={attraction.id}>
                    <button onClick={() => { addAttraction.mutate({ visitId: visitId!, attractionName: attraction.name }); }}
                      disabled={addAttraction.isPending}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-left disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                      <span className="text-sm text-gray-900 dark:text-gray-100">{attraction.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{attraction.entityType}</span>
                        {attraction.priority && (
                          <span className="text-xs font-medium text-gray-400 dark:text-gray-500">[{attraction.priority}]</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <button onClick={() => setShowAddAttraction(true)} disabled={!visit.parkName || addAttraction.isPending}
            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:border-disney-blue-400 hover:text-disney-blue-600 dark:hover:border-disney-blue-500 dark:hover:text-disney-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Add attractions
          </button>
        )}

        {queueTimes && queueTimes.length > 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 text-center">Auto-refreshes every 30 seconds</p>
        )}
      </div>
    </PullToRefresh>
  );
}
